import * as React from 'react';
import { useEffect, useState } from 'react';
import { Container, Nav, NavDropdown, NavItem, Navbar, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useCompanyAccountParams } from 'src/hooks/routerCompat';
import { Dispatch } from 'redux';
import { ensureUserSettings, putUserSettings } from '../../../actions/userSettingsActions';
import config from '../../../config';
import { ValidationErrors } from '../../../models/Errors';
import { AppState } from '../../../models/state/appState';
import { UserSettings, getDefaultUserSettingsState } from '../../../models/state/userSettings';
import { getUserSetting } from '../../../services/helpers';
import userManager from '../../../services/userManager';
import AccountMenu from '../../account/AccountMenu';
import CompanyTopNav from '../../company/CompanyTopNav';
import CompanyMenu from './CompanyMenu';
import HomeTopNav from './HomeTopNav';

// react-select lives behind this dialog, so it stays off the first page load
const UserSettingsDialog = React.lazy(() =>
  import('../../userSettings/UserSettingsForm').then((m) => ({ default: m.UserSettingsDialog }))
);

import NetworkStatusModal from '../../ui/modal/NetworkStatusModal';
import { getByCompanyId } from '../../../api/NetworkStatusApi';
import { getByCompanyIdAndAccountId } from '../../../api/NetworkStatusApi';
import { NetworkStatusItem, getNetworkStatusLabel } from '../../../../src/models/NetworkStatus';
import AlertIcon from '../../setup/components/AlertTriangle';

const AlertIconComponent = () => (
  <span className="alert-icon">
    <AlertIcon />
  </span>
);

const TopNavFilters = ({ onNavClick }: { onNavClick: () => void }) => {
  const { companyId, accountId } = useCompanyAccountParams();
  return (
    <React.Fragment>
      <CompanyMenu selectedCompanyId={companyId} />
      {companyId && <AccountMenu companyId={companyId} accountId={accountId} onNavClick={onNavClick} />}
    </React.Fragment>
  );
};

const HomeTopNavRoute = () => {
  const location = useLocation();
  return location.pathname === '/' ? <HomeTopNav /> : null;
};

const CompanyTopNavRoute = ({ onNavClick }: { onNavClick: () => void }) => {
  const { companyId, accountId } = useCompanyAccountParams();
  if (!companyId) {
    return null;
  }
  return <CompanyTopNav companyId={companyId} accountId={accountId} onNavClick={onNavClick} />;
};

type MenuMode = 'editUserSettings';

const getAccountIdFromPath = (pathname: string) => {
  const match = pathname.match(/\/company\/\d+\/account\/(\d+)/);
  if (!match) {
    return undefined;
  }

  const parsed = parseInt(match[1], 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const withForcedSimListDefaultViewIfDashboardHidden = (userSettings: UserSettings): UserSettings => {
  const hideDashboard =
    getUserSetting('hideDashboard', userSettings.userSettings)?.settingValue?.toLowerCase() === 'true';
  if (!hideDashboard) {
    return userSettings;
  }

  const userSettingsCopy: UserSettings = JSON.parse(JSON.stringify(userSettings));
  const existingDefaultView = userSettingsCopy.userSettings.find(
    (userSetting) => userSetting.name.toLowerCase() === 'defaultview'
  );

  if (existingDefaultView) {
    existingDefaultView.settingValue = 'SimList';
  } else {
    userSettingsCopy.userSettings.push({ name: 'DefaultView', settingValue: 'SimList' });
  }

  return userSettingsCopy;
};

export default function TopNav() {
  const dispatch: Dispatch<any> = useDispatch();
  const location = useLocation();
  const userName = useSelector<AppState, string | undefined>(
    (state) => state.loginDetails.user && state.loginDetails.user.name
  );
  const userId = useSelector<AppState, string | undefined>(
    (state) =>
      state.loginDetails.user && state.loginDetails.user.user && state.loginDetails.user.user.profile.sub
  );
  const subportalUser = useSelector<AppState, boolean | undefined>(
    (state) => state.loginDetails.user && state.loginDetails.user.subportalUser
  );
  const idToken = useSelector<AppState, string | undefined>(
    (state) => state.loginDetails.user && state.loginDetails.user.user && state.loginDetails.user.user.id_token
  );
  const savedUserSettings = useSelector<AppState, UserSettings | undefined>((state) => state.userSettings);
  const companyId = useSelector<AppState, number | undefined>((state) => state.filter.company.id);

  const [userSettings, setUserSettings] = useState<UserSettings>(getDefaultUserSettingsState());
  const [userSettingsErrors, setUserSettingsErrors] = useState<ValidationErrors>({});
  const [isSubmittingDialog, setIsSubmittingDialog] = useState(false);
  const [mode, setMode] = useState<MenuMode>();
  const [collapsed, setCollapsed] = useState(true);
  const [networkStatuses, setNetworkStatuses] = useState<NetworkStatusItem[]>([]);
  const [modalShow, setModalShow] = useState(false);
  const currentAccountId = getAccountIdFromPath(location.pathname);

  useEffect(() => {
    if (userId) {
      dispatch(ensureUserSettings(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    let cancelled = false;
    setNetworkStatuses([]);

    if (companyId) {
      const fetch =
        currentAccountId === undefined
          ? getByCompanyId(companyId)
          : getByCompanyIdAndAccountId(companyId, currentAccountId);

      fetch
        .catch(() => [])
        .then((statuses) => {
          if (!cancelled) {
            setNetworkStatuses(statuses);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [companyId, currentAccountId]);

  function onSave() {
    setIsSubmittingDialog(true);

    new Promise<any>((resolve, reject) => {
      dispatch(putUserSettings(withForcedSimListDefaultViewIfDashboardHidden(userSettings), resolve, reject));
    })
      .then(() => {
        setIsSubmittingDialog(false);
        setMode(undefined);
      })
      .catch((error) => {
        const validationErrors: ValidationErrors = error.validationErrors || {};
        setUserSettingsErrors(validationErrors);
        setIsSubmittingDialog(false);
        setMode(undefined);
      });
  }

  const navDropdownTitle: any = (
    <span className="username" aria-label="User settings">
      <i className="fa-duotone fa-user" /> <span>{userName}</span>
    </span>
  );

  return (
    <React.Fragment>
      <Navbar id="topNav" expand="lg" variant="light">
        <Container fluid>
          <Navbar.Toggle aria-controls="basic-navbar-nav" aria-label="Toggle navigation">
            <i className="fa-solid fa-bars mobile-menu-icon" aria-hidden="true" />
            <div className="brand-logo mobile"></div>
          </Navbar.Toggle>
          <Navbar.Brand>
            <div className="brand-logo desktop"></div>
          </Navbar.Brand>
          {subportalUser !== true && (
            <div className="selector-container">
              <TopNavFilters onNavClick={() => setCollapsed(true)} />
            </div>
          )}
          <Navbar.Collapse>
            <HomeTopNavRoute />
            <CompanyTopNavRoute onNavClick={() => setCollapsed(true)} />
          </Navbar.Collapse>
          <Nav className="navbar-right">
            {networkStatuses.length > 0 && (
              <OverlayTrigger
                key="bottom"
                placement="left"
                overlay={
                  <Tooltip id="alert-tooltip">
                    <ul className="tooltip-list">
                      {networkStatuses.map((networkStatus) => (
                        <li key={networkStatus.id}>
                          <strong>{getNetworkStatusLabel(networkStatus.status)}:</strong> {networkStatus.title}
                        </li>
                      ))}
                    </ul>
                  </Tooltip>
                }
              >
                <NavItem onClick={() => setModalShow(true)}>
                  <span className="help-icon">
                    <AlertIconComponent />
                  </span>
                </NavItem>
              </OverlayTrigger>
            )}
            <NavDropdown className="nav-dropdown" title={navDropdownTitle} id="nav-dropdown">
              <NavDropdown.Item href={`${config.AUTH_BASE_URL}/account/changepassword?returnUrl=${window.location}`}>
                <span>
                  <i className="fa-solid fa-key" />
                  Change Password
                </span>
              </NavDropdown.Item>
              <NavDropdown.Item
                onClick={() => {
                  if (savedUserSettings) {
                    setMode('editUserSettings');
                    setUserSettings(savedUserSettings);
                  }
                }}
              >
                <span>
                  <i className="fa-solid fa-user-edit" />
                  Edit User Settings
                </span>
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item
                className="help"
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  userManager.signoutRedirect({ id_token_hint: idToken });
                }}
              >
                <span>
                  <i className="fa-solid fa-sign-out-alt" />
                  Sign Out
                </span>
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Container>
      </Navbar>
      {mode === 'editUserSettings' && (
        <React.Suspense fallback={null}>
          <UserSettingsDialog
            userSettings={userSettings}
            errors={userSettingsErrors}
            show
            submitting={isSubmittingDialog}
            onInputChange={(name: string, value: React.Key) => {
              const userSettingsCopy: UserSettings = JSON.parse(JSON.stringify(userSettings));
              const foundSettings = userSettingsCopy.userSettings.filter(
                (userSetting) => userSetting.name.toLowerCase() === name.toLowerCase()
              );

              if (foundSettings.length > 0) {
                foundSettings.forEach((userSetting) => (userSetting.settingValue = value.toString()));
              } else {
                userSettingsCopy.userSettings.push({ name: name, settingValue: value.toString() });
              }

              setUserSettings(userSettingsCopy);
            }}
            onHide={() => setMode(undefined)}
            onSave={onSave}
          />
        </React.Suspense>
      )}
      <NetworkStatusModal
        modalShow={modalShow}
        handleExitModalClose={() => setModalShow(false)}
        networkStatuses={networkStatuses}
      />
    </React.Fragment>
  );
}
